import { Grid, IconButton } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import BasicDialog from "../../../components/base/BasicDialog";
import FilterTable from "../../../components/table/FilterTable";
import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";
import i18n from "../../../i18n";
import TableService from "../../../services/tableService";
import LoadingButton from "@mui/lab/LoadingButton";
import toast from "react-hot-toast";
import { SnackbarError } from "../../../components/base/SnackbarAlert/SnackbarError";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";

import DateService from "../../../services/dateService";

import { usePricing, usePublishLastPricingProductMutation } from "../../../services/productsService";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { ProductPricingEditDialog } from "./ProductPricingEditDialog";
import ConfirmationDialog from "../../../components/dialog/ConfirmationDialog/confirmationDialog";
import EditIcon from "@mui/icons-material/Edit";

const PRODUCT_PRICING_HISTORY_TABLE_CONFIG = [
    {
        name: "created",
        getValueCallback: (rowData) => {
            return DateService.convertDatetimeFromBackendToFormatYYYYMMDD(rowData.created);
        },
        label: i18n.t("products.ProductPricing.data"),
        headerSx: { whiteSpace: "break-spaces", minWidth: "100px" },
    },
    {
        name: "minimum_price_gross_raw",
        getValueCallback: (rowData) => {
            return rowData.minimum_price_gross_raw.toFixed(2);
        },
        label: i18n.t("products.ProductPricing.priceMinSale"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "minimum_price_per_sqm_gross",
        getValueCallback: (rowData) => {
            return rowData.minimum_price_per_sqm_gross.toFixed(2);
        },
        label: i18n.t("products.ProductPricing.priceMinSalePerSqm"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "base_price_gross_raw",
        getValueCallback: (rowData) => {
            return rowData.base_price_gross_raw.toFixed(2);
        },
        label: i18n.t("products.ProductPricing.priceMinBase"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "base_price_per_sqm_gross",
        getValueCallback: (rowData) => {
            return rowData.base_price_per_sqm_gross.toFixed(2);
        },
        label: i18n.t("products.ProductPricing.priceMinBasePerSQM"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "listed_price_gross_raw",
        getValueCallback: (rowData) => {
            return rowData.listed_price_gross_raw.toFixed(2);
        },
        label: i18n.t("products.ProductPricing.priceListed"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "listed_price_per_sqm_gross",
        getValueCallback: (rowData) => {
            return rowData.listed_price_per_sqm_gross.toFixed(2);
        },
        label: i18n.t("products.ProductPricing.priceListedPerSqm"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {

        name: "pricing_product_footage",
        getValueCallback: (rowData) => (rowData.pricing_product_footage ? `${rowData.pricing_product_footage} m²` : "-"),
        label: i18n.t("products.ProductPricing.footage"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "pricing_published",
        getValueCallback: (rowData) => {
            return rowData.pricing_published ? i18n.t("yes") : i18n.t("no");
        },
        label: i18n.t("products.ProductPricing.pricingPublished"),
        headerSx: { whiteSpace: "break-spaces" },
    },
    {
        name: "edit",
        getValueCallback: (rowData, onOpenEditPricingDialog, readOnly) => (
           readOnly ? null : <IconButton
                color={"primary"}
                onClick={(e) => onOpenEditPricingDialog(rowData)}
            >
                <EditIcon />
            </IconButton>
        ),
        label: i18n.t("edit"),
        headerSx: { whiteSpace: "break-spaces" },
    },
];

export function ProductPricingHistoryDialog(props) {
    const { t } = useTranslation();

    const { data: pricing } = usePricing({ pricing_product: props.productId });

    const mutationHandlers = {
        onError: (error) => {
            toast.custom(
                () => (
                    <SnackbarError message={t("products.ProductConfigurationDialog.publishPricingError")} />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
        onSuccess: () => {
            toast.custom(
                () => (
                    <SnackbarSuccess
                        message={t("products.ProductConfigurationDialog.publishPricingSuccessfully")}
                    />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
    }
    const { mutate: publishLastPricing, isPending: isPublishLastPricing } = usePublishLastPricingProductMutation(mutationHandlers);

    const { openDialog: openPublishPricingDialog, closeDialog: closePublishPricingDialog } =
        useDialogBetter();

    const onOpenPublishPricingDialog = () => {
        openPublishPricingDialog({
            Component: ConfirmationDialog,
            componentProps: {
                title: t("products.ProductConfigurationDialog.publishPricing"),
                content: t("products.ProductConfigurationDialog.publishLastPricingContent"),
                onYes: () => {
                    publishLastPricing({ productId: props.productId })
                    closePublishPricingDialog();
                },
                onNo: () => {
                    closePublishPricingDialog();
                },
            }})
    }

    const { openDialog: openEditPricingDialog, closeDialog: closeEditPricingDialog } =
        useDialogBetter();

    const onOpenEditPricingDialog = (pricingData) => {
        openPublishPricingDialog({
            Component: ProductPricingEditDialog,
            componentProps: {
                pricingData: pricingData
            }})
    }

    const prepatedData = useMemo(() => {
        if (!pricing) {
            return undefined;
        }

        return TableService.getPreparedDataForCollapsibleTable(
            PRODUCT_PRICING_HISTORY_TABLE_CONFIG,
            pricing,
            [],
            [],
            onOpenEditPricingDialog,
            props.readOnly,
        );
    }, [pricing]);

    const showPublishLastPricingButton = useMemo(() => {
        if (props.readOnly || !pricing || pricing.length === 0) {
            return false;
        }

        return !pricing[0]?.pricing_published

    }, [pricing]);

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign="center"
            contentAlign="start"
            title={t("products.ProductConfigurationDialog.pricingHistory")}
            titleSx={{"textTransform": "uppercase"}}
            maxWidth="lg"
            showDialogActions={false}
        >
        <Grid container justifyContent={"flex-end"} spacing={1}>
            {showPublishLastPricingButton && (
                <Grid item xs={4}>
                    <LoadingButton
                        disabled={false}
                        variant="contained"
                        onClick={onOpenPublishPricingDialog}
                        component="label"
                        loading={isPublishLastPricing}
                        fullWidth
                    >
                        {t("products.ProductConfigurationDialog.publishLastPricing")}
                    </LoadingButton>
                </Grid>
                        )}
                <Grid item xs={12}>
                    <LoaderWrapper showLoader={prepatedData === undefined}>
                        <FilterTable
                            // @ts-ignore
                            data={prepatedData || []}
                            withPagination={false}
                            style={{ maxHeight: "70vh" }}
                            showCleanFilterIcon={false}
                            showContextMenu={false}
                            showExportToFileButton={false}
                            headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                                PRODUCT_PRICING_HISTORY_TABLE_CONFIG,
                                { loading: false, value: {} },
                                [],
                                [],
                            )}
                            showDetailsIcon={false}
                            showCounterRecords={false}
                            showCheckbox={false}
                            showFilters={false}
                            setSelectedRowId={() => {}}
                            hideFiltersIconCol={true}
                        />
                    </LoaderWrapper>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
